import React, { useEffect, useState } from 'react';
import CheckinConfirmModal from './CheckinConfirmModal';
import ModalBody from '../modal/ModalBody';
import ModalHeader from '../modal/ModalHeader';
import type { SingleModalState } from '../../../types';

type CheckinModalProps = {
  onCheckIn: (sessionType: 'remote' | 'remote_sick' | 'sick' | 'business_trip') => void;
  loading: boolean;
  error: string | null;
  needsRemoteCheckin: boolean;
  isUserClient: boolean;
  modal: SingleModalState;
  onCloseModal: (modal: SingleModalState) => void;
  onOpenModal: (modal: SingleModalState) => void;
  modalId: string;
};

const CheckinModal: React.FC<CheckinModalProps> = ({
  onCheckIn,
  loading,
  error,
  needsRemoteCheckin,
  isUserClient,
  modal,
  onCloseModal,
  onOpenModal,
  modalId
}) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingSessionType, setPendingSessionType] = useState<'remote_sick' | 'sick' | 'business_trip' | null>(null);
  const [selectedSessionType, setSelectedSessionType] = useState<'remote' | 'remote_sick' | 'sick' | 'business_trip'>('remote');
  const [isDismissed, setIsDismissed] = useState(false);

  // Automatically open modal when check-in is needed and not dismissed
  useEffect(() => {
    if (needsRemoteCheckin && !isUserClient && !isDismissed && !modal?.display) {
      onOpenModal({ id: modalId, display: true, disableButtons: false });
    }
  }, [needsRemoteCheckin, isUserClient, isDismissed, modal?.display, modalId, onOpenModal]);

  if (!needsRemoteCheckin || isUserClient) {
    return null;
  }

  const showMainModal = modal?.display || false;

  const handleCheckIn = () => {
    if (selectedSessionType === 'remote') {
      onCheckIn(selectedSessionType);
    } else {
      // Show confirmation modal for sick leave and business trip types
      setPendingSessionType(selectedSessionType);
      setShowConfirmModal(true);
    }
  };

  const handleConfirmCheckIn = () => {
    if (pendingSessionType) {
      onCheckIn(pendingSessionType);
    }
    setShowConfirmModal(false);
    setPendingSessionType(null);
  };

  const handleCancelCheckIn = () => {
    setShowConfirmModal(false);
    setPendingSessionType(null);
  };

  const handleClose = () => {
    setIsDismissed(true);
    onCloseModal({ id: modalId, display: false, disableButtons: false });
  };

  if (!showMainModal) {
    return null;
  }

  // Format current date
  const today = new Date();
  const formattedDate = today.toLocaleDateString('hr-HR', {
    month: 'short',
    day: 'numeric'
  });

  return (
    <>
      {/* Main Check-in Modal */}
      <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '400px' }}>
          <div className="modal-content">
            <ModalHeader title="Check in" onCloseClick={handleClose} />
            <ModalBody>
              <div className="mb-3">
                <label htmlFor="sessionType" className="form-label" style={{ fontWeight: 500, marginBottom: '0.5rem', display: 'block' }}>
                  Check-in *
                </label>
                <select
                  id="sessionType"
                  className="form-select"
                  value={selectedSessionType}
                  onChange={(e) => setSelectedSessionType(e.target.value as 'remote' | 'remote_sick' | 'sick' | 'business_trip')}
                  disabled={loading}
                  style={{
                    padding: '0.5rem 0.75rem',
                    fontSize: '1rem',
                    borderRadius: '0.375rem',
                    border: '1px solid #ced4da',
                    backgroundColor: '#fff',
                    width: '100%',
                    display: 'block'
                  }}
                >
                  <option value="remote">Remote Work</option>
                  <option value="remote_sick">Remote Sick</option>
                  <option value="sick">Sick Day</option>
                  <option value="business_trip">Business Trip</option>
                </select>
              </div>
              <div className="mb-3 text-muted" style={{ fontSize: '0.875rem' }}>
                Today, {formattedDate}
              </div>
              {error && (
                <div className="alert alert-danger py-2 mb-3">
                  {error}
                </div>
              )}
              <button
                className="btn btn-primary primary-color w-100"
                onClick={handleCheckIn}
                disabled={loading}
                type="button"
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Checking in...
                  </>
                ) : (
                  'Check-in'
                )}
              </button>
            </ModalBody>
          </div>
        </div>
      </div>

      {/* Confirmation Modal for Sick Leave and Business Trip Types */}
      <CheckinConfirmModal
        show={showConfirmModal}
        sessionType={pendingSessionType}
        onConfirm={handleConfirmCheckIn}
        onCancel={handleCancelCheckIn}
      />
    </>
  );
};

export default CheckinModal;
